import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/ui/EmptyState';
import { Pill, stateLabel, stateToTone } from '@/components/ui/Pill';
import { type RevenueTrendItem, adminApi } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';

export const Route = createFileRoute('/dashboard')({
  beforeLoad: async ({ location }) => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login', search: { next: location.pathname } as Record<string, string> });
    }
  },
  component: DashboardPage,
});

function fmtEur(n: number): string {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

// ── Revenue chart SVG ───────────────────────────────────────────────────────

function RevenueChart({ items }: { items: RevenueTrendItem[] }) {
  const W = 680;
  const H = 180;
  const PAD_L = 52;
  const PAD_B = 24;
  const PAD_T = 12;
  const chartW = W - PAD_L - 8;
  const chartH = H - PAD_T - PAD_B;

  const maxEur = Math.max(...items.map((d) => d.eur), 1);
  // Round up to a nice number
  const axisMax = Math.ceil(maxEur / 100) * 100;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(axisMax * f));

  const xFor = (i: number) => PAD_L + (i / (items.length - 1)) * chartW;
  const yFor = (eur: number) => PAD_T + chartH - (eur / axisMax) * chartH;

  const points = items.map((d, i) => `${xFor(i)},${yFor(d.eur)}`).join(' ');

  // Filled area under the line
  const areaPoints = `${xFor(0)},${PAD_T + chartH} ${items.map((d, i) => `${xFor(i)},${yFor(d.eur)}`).join(' ')} ${xFor(items.length - 1)},${PAD_T + chartH}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      style={{ marginTop: 16, display: 'block' }}
      aria-hidden="true"
    >
      {/* Grid lines */}
      {ticks.map((v) => {
        const y = yFor(v);
        return (
          <g key={v}>
            <line x1={PAD_L} x2={W - 8} y1={y} y2={y} stroke="var(--lv-line)" strokeWidth="1" />
            <text
              x={PAD_L - 6}
              y={y + 4}
              fontSize="9"
              fill="var(--lv-gray-500)"
              textAnchor="end"
              fontFamily="Barlow Condensed, sans-serif"
              fontWeight="700"
            >
              {v === 0 ? '0' : `${v / 1000 >= 1 ? `${(v / 1000).toFixed(1)}k` : v}€`}
            </text>
          </g>
        );
      })}

      {/* Filled area */}
      <polygon points={areaPoints} fill="var(--lv-brand)" opacity="0.07" />

      {/* Line */}
      <polyline
        points={points}
        fill="none"
        stroke="var(--lv-brand)"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Data points */}
      {items.map((d, i) => (
        <circle
          key={d.day}
          cx={xFor(i)}
          cy={yFor(d.eur)}
          r={d.eur > 0 ? 3 : 2}
          fill={d.eur > 0 ? 'var(--lv-brand)' : 'var(--lv-line)'}
          stroke="#fff"
          strokeWidth="1.5"
        />
      ))}

      {/* X-axis baseline */}
      <line
        x1={PAD_L}
        x2={W - 8}
        y1={PAD_T + chartH}
        y2={PAD_T + chartH}
        stroke="var(--lv-line)"
        strokeWidth="1"
      />

      {/* Date labels — every other tick for readability */}
      {items.map((d, i) => {
        if (items.length > 10 && i % 2 !== 0) return null;
        const dayNum = d.day.slice(8, 10);
        return (
          <text
            key={d.day}
            x={xFor(i)}
            y={H - 6}
            fontSize="9"
            fill="var(--lv-gray-500)"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
          >
            {dayNum}
          </text>
        );
      })}
    </svg>
  );
}

// ── Today date helpers ──────────────────────────────────────────────────────

function todayLabel(): string {
  return new Date()
    .toLocaleDateString('pt-PT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    .toUpperCase();
}

function greetingByHour(): string {
  const h = new Date().getHours();
  if (h < 12) return 'BOM DIA';
  if (h < 18) return 'BOA TARDE';
  return 'BOA NOITE';
}

// ── Main page ───────────────────────────────────────────────────────────────

function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => adminApi.dashboard(),
    refetchInterval: 60_000,
  });

  const { data: trendData } = useQuery({
    queryKey: ['admin', 'revenue-trend', 14],
    queryFn: () => adminApi.revenueTrend(14),
    refetchInterval: 60_000,
  });

  // Compute 14-day total from trend
  const trend14Total = trendData?.items.reduce((s, d) => s + d.eur, 0) ?? 0;

  return (
    <Shell page={`${greetingByHour()}.`} sub={`DASHBOARD · ${todayLabel()}`}>
      {/* ── Row 1: 4 KPI cards ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          background: '#fff',
          border: '1px solid var(--lv-line)',
        }}
      >
        {[
          {
            label: 'RESERVAS ACTIVAS',
            value: isLoading ? <Skeleton /> : String(data?.activeBookings ?? 0),
            delta: `${data?.upcomingBookings ?? 0} confirmadas`,
            color: 'var(--lv-ink)',
            deltaColor: 'var(--lv-ok)',
          },
          {
            label: 'RECEITA · 14 DIAS',
            value: isLoading ? <Skeleton /> : fmtEur(trend14Total),
            delta: `${data?.depositsCollectedLast30d ?? 0} sinais (30d)`,
            color: 'var(--lv-ink)',
            deltaColor: 'var(--lv-ok)',
          },
          {
            // fleetActive = published vehicles, NOT vehicles on the road — say so.
            label: 'FROTA ACTIVA',
            value: isLoading ? <Skeleton /> : `${data?.fleetActive ?? 0}/${data?.fleetTotal ?? 0}`,
            delta: `${data?.activeBookings ?? 0} em curso agora`,
            color: 'var(--lv-brand)',
            deltaColor: 'var(--lv-brand)',
          },
          {
            label: 'AVALIAÇÕES PENDENTES',
            value: isLoading ? <Skeleton /> : String(data?.reviewsPending ?? 0),
            delta: (data?.reviewsPending ?? 0) > 0 ? 'a moderar' : 'sem pendentes',
            color: 'var(--lv-ink)',
            deltaColor: (data?.reviewsPending ?? 0) > 0 ? 'var(--lv-warn)' : 'var(--lv-ok)',
          },
        ].map((kpi, i) => (
          <div
            key={kpi.label}
            style={{
              padding: '22px 24px',
              borderRight: i < 3 ? '1px solid var(--lv-line)' : 'none',
            }}
          >
            <div className="label" style={{ color: 'var(--lv-gray-500)' }}>
              {kpi.label}
            </div>
            <div
              className="display"
              style={{
                fontSize: 52,
                color: kpi.color,
                lineHeight: 1,
                marginTop: 8,
              }}
            >
              {kpi.value}
            </div>
            <div
              style={{
                fontSize: 11,
                color: kpi.deltaColor,
                fontWeight: 700,
                marginTop: 6,
              }}
            >
              {kpi.delta}
            </div>
          </div>
        ))}
      </div>

      {/* ── Row 2: Revenue chart + Attention queue ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr',
          gap: 16,
          marginTop: 16,
        }}
      >
        {/* Revenue chart card */}
        <div className="ed-card" style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <div className="label" style={{ color: 'var(--lv-brand)' }}>
                RECEITA · 14 DIAS
              </div>
              <div className="display" style={{ fontSize: 44, marginTop: 6, lineHeight: 1 }}>
                {fmtEur(trend14Total)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--lv-gray-500)', marginTop: 4 }}>
                Receita realizada (pagamentos confirmados)
              </div>
            </div>
          </div>

          {trendData?.items && trendData.items.length > 0 && trend14Total > 0 ? (
            <RevenueChart items={trendData.items} />
          ) : (
            <div
              style={{
                marginTop: 16,
                height: 180,
                background: 'var(--lv-surface-soft)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                color: 'var(--lv-gray-500)',
              }}
            >
              {trendData ? 'Sem receita registada nos últimos 14 dias.' : 'A carregar dados…'}
            </div>
          )}
        </div>

        {/* Attention queue */}
        <AttentionQueue
          reviewsPending={data?.reviewsPending ?? 0}
          paymentsFailed={data?.paymentsFailed ?? 0}
        />
      </div>

      {/* ── Row 3: Fleet status ── */}
      <FleetStatusRow
        active={data?.fleetActive ?? 0}
        total={data?.fleetTotal ?? 0}
        inUse={data?.activeBookings ?? 0}
        maintenance={data?.fleetMaintenance ?? 0}
      />
    </Shell>
  );
}

// ── Attention queue ─────────────────────────────────────────────────────────

function AttentionQueue({
  reviewsPending,
  paymentsFailed,
}: { reviewsPending: number; paymentsFailed: number }) {
  const items: {
    label: string;
    sub: string;
    to: string;
    btnLabel: string;
    color: string;
    count?: number;
  }[] = [];

  if (reviewsPending > 0) {
    items.push({
      label: 'AVALIAÇÕES PENDENTES',
      sub: `${reviewsPending} avaliação(ões) a moderar`,
      to: '/reviews',
      btnLabel: 'MODERAR',
      color: 'var(--lv-brand)',
      count: reviewsPending,
    });
  }

  if (paymentsFailed > 0) {
    items.push({
      label: 'PAGAMENTOS FALHADOS',
      sub: `${paymentsFailed} pagamento(s) Vinti4 falhado(s)`,
      to: '/payments',
      btnLabel: 'VERIFICAR',
      color: 'var(--lv-warn)',
      count: paymentsFailed,
    });
  }

  return (
    <div className="ed-card" style={{ padding: 24 }}>
      <div className="label" style={{ color: 'var(--lv-brand)' }}>
        PRECISA DE ATENÇÃO
      </div>
      <div className="display" style={{ fontSize: 24, marginTop: 4, lineHeight: 1 }}>
        {items.length === 0 ? 'TUDO EM DIA' : `${items.length} EM FILA`}
      </div>
      <div className="h-rule" style={{ marginTop: 12, marginBottom: 16 }} />
      {items.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--lv-gray-500)' }}>
          Sem avaliações a moderar nem pagamentos falhados.
        </div>
      )}

      <div style={{ display: 'grid', gap: 8 }}>
        {items.map((item) => (
          <div
            key={item.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 14,
              background: 'var(--lv-surface-soft)',
              borderLeft: `4px solid ${item.color}`,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                {item.count !== undefined && (
                  <span
                    style={{
                      display: 'inline-block',
                      background: item.color,
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 800,
                      padding: '1px 6px',
                      marginRight: 6,
                    }}
                  >
                    {item.count}
                  </span>
                )}
                {item.label}
              </div>
              <div style={{ fontSize: 11, color: 'var(--lv-gray-500)', marginTop: 2 }}>{item.sub}</div>
            </div>
            <Link
              to={item.to}
              style={{
                background: 'transparent',
                border: '1px solid var(--lv-line)',
                color: 'var(--lv-ink)',
                padding: '6px 12px',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {item.btnLabel}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Fleet status summary row ────────────────────────────────────────────────

function FleetStatusRow({
  active,
  total,
  inUse,
  maintenance,
}: {
  active: number;
  total: number;
  inUse: number;
  maintenance: number;
}) {
  // Available = published vehicles not currently out on an in-progress booking.
  const free = Math.max(0, active - inUse);

  const cols = [
    { label: 'FROTA TOTAL', value: total, color: 'var(--lv-ink)' },
    { label: 'EM CURSO', value: inUse, color: 'var(--lv-brand)' },
    { label: 'DISPONÍVEL', value: free, color: 'var(--lv-ok)' },
    { label: 'EM MANUTENÇÃO', value: maintenance, color: 'var(--lv-warn)' },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          background: '#fff',
          border: '1px solid var(--lv-line)',
        }}
      >
        {cols.map((c, i) => (
          <div
            key={c.label}
            style={{
              padding: '20px 24px',
              borderRight: i < 3 ? '1px solid var(--lv-line)' : 'none',
            }}
          >
            <div className="label" style={{ color: 'var(--lv-gray-500)' }}>
              {c.label}
            </div>
            <div className="display" style={{ fontSize: 44, color: c.color, lineHeight: 1, marginTop: 8 }}>
              {c.value}
            </div>
          </div>
        ))}
      </div>

      {/* Quick navigation links */}
      <div
        style={{
          marginTop: 16,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 12,
        }}
      >
        {[
          {
            label: 'RESERVAS',
            sub: 'Gerir e detalhar reservas',
            to: '/bookings',
          },
          {
            label: 'FROTA',
            sub: 'Viaturas, fotos e preços',
            to: '/fleet',
          },
          {
            label: 'CLIENTES',
            sub: 'Directório e histórico',
            to: '/customers',
          },
        ].map((card) => (
          <Link
            key={card.to}
            to={card.to}
            style={{
              background: '#fff',
              border: '1px solid var(--lv-line)',
              padding: '18px 20px',
              textDecoration: 'none',
              color: 'var(--lv-ink)',
              display: 'block',
              transition: 'border-color 0.12s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--lv-brand)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--lv-line)';
            }}
          >
            <div className="label" style={{ color: 'var(--lv-brand)' }}>
              {card.label}
            </div>
            <div style={{ fontSize: 12, color: 'var(--lv-gray-500)', marginTop: 4 }}>{card.sub}</div>
            <div
              style={{
                marginTop: 10,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: 'var(--lv-brand)',
              }}
            >
              VER →
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Pill re-exported here just to keep the import used (TypeScript)
export { Pill, stateLabel, stateToTone };
